/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React, { useState } from 'react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Calculator, BarChart3, Trophy, FileText, Ticket } from 'lucide-react';
import MedicalCalculators from '@/components/toolkit/MedicalCalculators';
import BenchmarkPanel from '@/components/toolkit/BenchmarkPanel';
import BadgesGallery from '@/components/toolkit/BadgesGallery';
import ModulisticaLibrary from '@/components/toolkit/ModulisticaLibrary';
import InviteCodesPanel from '@/components/toolkit/InviteCodesPanel';
import ColleagueReferralPanel from '@/components/growth/ColleagueReferralPanel';

/**
 * Pro Toolkit — four "magnet" free features for healthcare professionals:
 * 1. Medical calculators (offline, zero LLM)
 * 2. Anonymous benchmark vs peers
 * 3. Achievement badges
 * 4. Ready-to-print Italian modulistica (PDF)
 */
const ProToolkitPage = () => {
  const [tab, setTab] = useState('calculators');

  return (
    <div className="space-y-4" data-testid="pro-toolkit-page">
      <div>
        <h1 className="font-display font-bold text-2xl md:text-3xl flex items-center gap-2">
          Kit del Professionista
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Strumenti clinici, benchmark e modulistica — tutto in un unico posto.
        </p>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="grid grid-cols-3 md:grid-cols-5 h-auto">
          <TabsTrigger value="calculators" className="gap-1.5" data-testid="tab-calculators">
            <Calculator className="h-3.5 w-3.5" /> Calcolatori
          </TabsTrigger>
          <TabsTrigger value="benchmark" className="gap-1.5" data-testid="tab-benchmark">
            <BarChart3 className="h-3.5 w-3.5" /> Benchmark
          </TabsTrigger>
          <TabsTrigger value="badges" className="gap-1.5" data-testid="tab-badges">
            <Trophy className="h-3.5 w-3.5" /> Badge
          </TabsTrigger>
          <TabsTrigger value="modulistica" className="gap-1.5" data-testid="tab-modulistica">
            <FileText className="h-3.5 w-3.5" /> Modulistica
          </TabsTrigger>
          <TabsTrigger value="invites" className="gap-1.5" data-testid="tab-invites">
            <Ticket className="h-3.5 w-3.5" /> Inviti
          </TabsTrigger>
        </TabsList>

        <TabsContent value="calculators" className="mt-4">
          <MedicalCalculators />
        </TabsContent>
        <TabsContent value="benchmark" className="mt-4">
          <BenchmarkPanel />
        </TabsContent>
        <TabsContent value="badges" className="mt-4">
          <BadgesGallery />
        </TabsContent>
        <TabsContent value="modulistica" className="mt-4">
          <ModulisticaLibrary />
        </TabsContent>
        <TabsContent value="invites" className="mt-4">
          <InviteCodesPanel />
          <ColleagueReferralPanel />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ProToolkitPage;
